// Lint pots (pre-strainers) (owner, 2026-10-19): "these are lint pots or pre strainers, basically it's a chamber in the
// shape of a cylinder that has 2 or more inlets and outlets; the inlets and outlets need to be configurable and have
// pipe functions as per the other components".
//
// 3.16.0 (owner, 2026-10-20, with a scan of six JagPoly configurations): "add a subclass for JagPoly ... allow the size
// of the lint pot, the heights of the inlets and outlets, the diameter of the inlets and outlets, and yes record height
// and do an elevation view". So a lint pot also has a HEIGHT (base to the top of the lid), each end has the HEIGHT of
// its centre above the base, a pot can be drawn as a PLAN or an ELEVATION, and a pot can have the JagPoly look.
//
// PLAN VIEW. The chamber is a circle of the chamber diameter. Each end is a short straight pipe out of the chamber
// wall: two side lines from the wall to the end face, the flange at the end face and a dash-dot centre line. A dash-dot
// cross marks the centre of the chamber. The centre is the turning point. A JagPoly pot also has its lid ring (16
// notches, two handle slots) round the chamber; the end lines start at the ring, and each flange is a Table E flange.
//
// ELEVATION VIEW: the pot seen from the FRONT, that is from the bottom of the plan looking up the page. The base is
// at y = 0 (the turning point is the centre of the base) and up the page is up. The chamber is a rectangle, the lid on
// top. An end that goes to the side is a stub at its height with its flange; an end that comes towards you shows its
// flange face (an ellipse, a circle when it points straight at you, with its bolt holes); an end that goes away from
// you is dashed where the chamber hides it. Ends that point at you or away from you are VERTICAL ends: a pipe there
// leaves the drawing, so it starts with a floor penetration circle (src/pipes/api.ts). 3.31.0: an elevation can also be
// seen from the right, the back or the left of the plan (spec.seenFrom); the end angles stay the plan directions.
//
// Each end has: inlet or outlet, a size (mm), an angle, a length (mm, from the chamber wall to the end face) and a
// height (mm, its centre above the base; absent = the middle of the chamber).
// ANGLE: degrees, clockwise on the screen from "to the right", as the Rotation box turns: 0 = right, 90 = down (towards
// the person who looks at the elevation), 180 = left, 270 = up. meta.lintpot holds the angles in the pot's OWN frame.
// A turn of a PLAN turns its ends (meta.ports) with it, and src/pipes/lintPot.ts reads the turn back from the ends. A
// turn of an ELEVATION turns the picture; the angles stay the plan directions.
//
// The group: meta.fitting = { kind: "lintpot", size: first inlet, size2: first outlet } (library filters and names);
// meta.lintpot = LintPotSpec; meta.ports = one end each, in the order of spec.ends (a pipe is attached to an end by
// its index); meta.pivot = the centre (plan) or the centre of the base (elevation).
import type { Vec } from "../core/geometry";
import type { Entity, Style } from "../doc/entities";
import type { LibraryItem } from "./library";
import type { FittingPortData } from "../pipes/api";
import { CENTRE, OUTLINE } from "./fittings";
import { tableEFlange } from "./flanges";

export type EndRole = "inlet" | "outlet";
export interface LintPotEnd {
  role: EndRole;
  /** Nominal size, mm (the drawn width). */
  size: number;
  /** Degrees, clockwise on screen from "to the right". */
  angle: number;
  /** From the chamber wall to the end face, mm. */
  length: number;
  /** 3.16.0: the height of the centre of the end above the base, mm. Absent = the middle of the chamber. */
  height?: number;
  /**
   * 3.16.1: a BEND on the end (owner, 2026-10-20: a lint pot whose two outlets leave the chamber at an angle and turn
   * so both flanges face the same way; "the spokes or outlets and inlets lengths need to be changeable"). The end leaves
   * the chamber at `angle` for `length` (the spoke, chamber wall to the bend), turns, and runs `bend.length` more to its
   * face, which faces `bend.angle` (degrees, as `angle`). Absent = a straight end.
   */
  bend?: { angle: number; length: number };
}
export type LintPotView = "plan" | "elevation";
export interface LintPotSpec {
  /** Chamber diameter, mm. */
  diameter: number;
  ends: LintPotEnd[];
  /** 3.16.0: from the base to the top of the lid, mm. Absent = the default for the make (lintPotHeight). */
  height?: number;
  /** 3.16.0: "jagpoly" = the JagPoly look (lid ring, eye bolts, Table E flanges). Absent = a plain lint pot. */
  make?: "jagpoly";
  /** 3.16.0: the maker's model words, for example "30L". */
  model?: string;
  /** 3.16.0: absent or "plan" = seen from above; "elevation" = seen from the front. */
  view?: LintPotView;
  /**
   * 3.31.0: the side of the plan an ELEVATION is seen from, degrees as an end angle: 90 = the bottom of the plan (the
   * front), 0 = the right, 270 = the top (the back), 180 = the left. Absent = 90. A plan ignores it. Owner, 2026-10-27,
   * with the JagPoly drawing LLPD-050225160-S7 (front, side and back elevations): "one for plan view, the other elevations".
   */
  seenFrom?: number;
}

export const LINTPOT_CATEGORY = "Pipe fittings · Lint pots";
export const JAGPOLY_CATEGORY = "Pipe fittings · Lint pots · JagPoly";
/** Limits that keep the drawing sensible. */
export const LINTPOT_LIMITS = { minDiameter: 100, maxDiameter: 3000, minEnds: 2, maxEnds: 12, minLength: 10, maxLength: 5000, endShare: 0.8, minHeight: 100, maxHeight: 6000 } as const;

const rad = (deg: number) => (deg * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;
const round = (v: number) => Math.round(v * 1000) / 1000;
const rp = (p: Vec): Vec => ({ x: round(p.x), y: round(p.y) });
/** An angle in 0 ≤ a < 360. */
export const normAngle = (a: number): number => {
  const r = ((a % 360) + 360) % 360;
  return Math.abs(r - 360) < 1e-9 ? 0 : r;
};

const isJag = (spec: LintPotSpec): boolean => spec.make === "jagpoly";

/** 3.31.0: the sides an elevation can be seen from, in the order of the Inspector list. */
export const SEEN_FROM: Array<{ angle: number; label: string; words: string }> = [
  { angle: 90, label: "Front (the bottom of the plan)", words: "" },
  { angle: 0, label: "Right side", words: " from the right" },
  { angle: 270, label: "Back (the top of the plan)", words: " from the back" },
  { angle: 180, label: "Left side", words: " from the left" }
];

/** 3.31.0: the side an elevation is seen from, 0 ≤ a < 360 (90 = the front). */
export const seenFromAngle = (spec: LintPotSpec): number => normAngle(spec.seenFrom ?? 90);

/** 3.31.0: "" (the front), " from the right", " from the back", " from the left", or " from 30°". */
export function seenFromWords(spec: LintPotSpec): string {
  const a = seenFromAngle(spec);
  return SEEN_FROM.find((s) => Math.abs(s.angle - a) < 1e-9)?.words ?? ` from ${Math.round(a * 10) / 10}°`;
}

/** A default length for an end of size d: never less than 100 mm (a JagPoly end clears the lid ring). */
export const defaultEndLength = (d: number, spec?: Pick<LintPotSpec, "make">): number =>
  spec?.make === "jagpoly" ? Math.round((0.6 * d + 110) / 10) * 10 : Math.max(100, Math.round(d / 10) * 10);

/** The height of the pot, base to the top of the lid, mm: the value, else 2.6 × the diameter (JagPoly) or 1.5 × (plain). */
export const lintPotHeight = (spec: LintPotSpec): number => spec.height ?? Math.round(spec.diameter * (isJag(spec) ? 2.6 : 1.5));

/** The lid: the radius of the JagPoly lid ring, and the height of the lid on top of the chamber, mm. */
function lid(spec: LintPotSpec) {
  const D = spec.diameter;
  return isJag(spec)
    ? { ring: 0.7 * D, stack: Math.max(60, 0.25 * D), plate: Math.max(10, 0.04 * D), base: Math.max(8, 0.03 * D) }
    : { ring: 0.55 * D, stack: Math.max(12, 0.04 * D), plate: Math.max(12, 0.04 * D), base: 0 };
}

/** The top of the chamber (the bottom of the lid) above the base, mm. */
export const chamberTop = (spec: LintPotSpec): number => lintPotHeight(spec) - lid(spec).stack;

/** The height of an end: the value, else the middle of the chamber. */
export const endHeight = (spec: LintPotSpec, e: LintPotEnd): number => e.height ?? Math.round(chamberTop(spec) / 2);

/** The flange of an end: Table E on a JagPoly pot; on a plain pot a plate 1.2 × the size, as before 3.16.0. */
function endFlange(spec: LintPotSpec, size: number): { od: number; t: number; pcd: number; n: number; hole: number } {
  if (isJag(spec)) return tableEFlange(size);
  return { od: 1.2 * size, t: 0, pcd: 0, n: 0, hole: 0 };
}

/** The least angle between the centre lines of two ends that keeps them apart, degrees. */
export function minGap(diameter: number, a: number, b: number): number {
  const R = diameter / 2;
  const half = (d: number) => deg(Math.asin(Math.min(1, d / 2 / R)));
  return half(a) + half(b) + 2;
}

/** A sentence that says what is wrong with a lint pot, or null when it can be drawn. */
export function lintPotProblem(spec: LintPotSpec): string | null {
  const L = LINTPOT_LIMITS;
  if (!Number.isFinite(spec.diameter) || spec.diameter < L.minDiameter || spec.diameter > L.maxDiameter) {
    return `The chamber diameter must be from ${L.minDiameter} to ${L.maxDiameter} mm.`;
  }
  if (spec.height !== undefined && (!Number.isFinite(spec.height) || spec.height < L.minHeight || spec.height > L.maxHeight)) {
    return `The height must be from ${L.minHeight} to ${L.maxHeight} mm.`;
  }
  if (spec.ends.length < L.minEnds) return `A lint pot has ${L.minEnds} ends or more.`;
  if (spec.ends.length > L.maxEnds) return `A lint pot has ${L.maxEnds} ends or fewer.`;
  const largest = Math.floor(spec.diameter * L.endShare);
  const top = chamberTop(spec);
  const underRing = isJag(spec) ? lid(spec).ring - spec.diameter / 2 : 0;
  for (const [i, e] of spec.ends.entries()) {
    if (e.role !== "inlet" && e.role !== "outlet") return `End ${i + 1} must be an inlet or an outlet.`;
    if (!Number.isFinite(e.size) || e.size <= 0) return `End ${i + 1} needs a size.`;
    if (e.size > largest) return `End ${i + 1} (${e.size} mm) is too large for a ${spec.diameter} mm chamber. The largest end is ${largest} mm.`;
    if (!Number.isFinite(e.angle)) return `End ${i + 1} needs an angle.`;
    if (!Number.isFinite(e.length) || e.length < L.minLength || e.length > L.maxLength) return `The length of end ${i + 1} must be from ${L.minLength} to ${L.maxLength} mm.`;
    if (underRing > 0 && e.length < underRing + 10) return `End ${i + 1} must be at least ${Math.ceil(underRing + 10)} mm long: its flange must be outside the lid ring.`;
    if (e.bend) {
      if (!Number.isFinite(e.bend.angle) || !Number.isFinite(e.bend.length)) return `The bend of end ${i + 1} needs an angle and a length.`;
      const turn = Math.abs(bendTurn(e));
      if (turn < 10 || turn > 135) return `The bend of end ${i + 1} must turn from 10° to 135°. Now it turns ${Math.round(turn)}°.`;
      const cut = (e.size / 2) * Math.tan(rad(turn) / 2); // the inside of the bend reaches back this far
      if (e.length < cut + 10) return `The length of end ${i + 1} to its bend must be at least ${Math.ceil(cut + 10)} mm.`;
      const need = cut + (isJag(spec) ? tableEFlange(e.size).t : 0) + 10;
      if (e.bend.length < need || e.bend.length > L.maxLength) return `The length of end ${i + 1} after its bend must be from ${Math.ceil(need)} to ${L.maxLength} mm.`;
    }
    if (e.height !== undefined) {
      if (!Number.isFinite(e.height)) return `End ${i + 1} needs a height.`;
      if (e.height - e.size / 2 < 0 || e.height + e.size / 2 > top) {
        return `The height of end ${i + 1} must be from ${Math.ceil(e.size / 2)} to ${Math.floor(top - e.size / 2)} mm: the end must be inside the chamber wall (the chamber is ${Math.round(top)} mm high under the lid).`;
      }
    }
  }
  for (let i = 0; i < spec.ends.length; i++) {
    for (let j = i + 1; j < spec.ends.length; j++) {
      const a = spec.ends[i]!;
      const b = spec.ends[j]!;
      const d = Math.abs(normAngle(a.angle - b.angle));
      const gap = Math.min(d, 360 - d);
      const need = minGap(spec.diameter, a.size, b.size);
      // ends at different heights pass each other
      const apart = Math.abs(endHeight(spec, a) - endHeight(spec, b)) >= (a.size + b.size) / 2 + 10;
      if (gap < need - 1e-9 && !apart) return `End ${i + 1} and end ${j + 1} touch. Leave at least ${Math.ceil(need)}° between them, or put them at different heights.`;
    }
  }
  return null;
}

/** The parts of a lint pot, with no ids yet. */
interface Part {
  kind: "circle" | "line";
  centre: boolean;
  /** drawn dashed: hidden behind the chamber (elevation) */
  hidden?: boolean;
  closed?: boolean;
  points?: Vec[];
  center?: Vec;
  r?: number;
}

/** Points round an ellipse (centre c, half widths rx, ry), closed. */
function ellipse(c: Vec, rx: number, ry: number, n = 48): Vec[] {
  const out: Vec[] = [];
  for (let i = 0; i < n; i++) {
    const t = (2 * Math.PI * i) / n;
    out.push({ x: c.x + rx * Math.cos(t), y: c.y + ry * Math.sin(t) });
  }
  return out;
}

/**
 * The parts and ends of a lint pot. `at` = the centre (plan) or the centre of the base (elevation). `turnDeg`: a plan
 * adds it to every end angle; an elevation turns its picture by it (the end angles stay the plan directions).
 */
export function lintPotParts(spec: LintPotSpec, at: Vec = { x: 0, y: 0 }, turnDeg = 0): { parts: Part[]; ports: FittingPortData[] } {
  return spec.view === "elevation" ? elevationParts(spec, at, turnDeg) : planParts(spec, at, turnDeg);
}

function planParts(spec: LintPotSpec, at: Vec, turnDeg: number): { parts: Part[]; ports: FittingPortData[] } {
  const R = spec.diameter / 2;
  const c = at;
  const jag = isJag(spec);
  const lidRing = lid(spec).ring;
  const parts: Part[] = [{ kind: "circle", centre: false, center: rp(c), r: R }];
  // the centre cross, turned with the pot
  const ux = { x: Math.cos(rad(turnDeg)), y: Math.sin(rad(turnDeg)) };
  const uy = { x: -ux.y, y: ux.x };
  const k = R * 0.25;
  parts.push({ kind: "line", centre: true, points: [rp({ x: c.x - ux.x * k, y: c.y - ux.y * k }), rp({ x: c.x + ux.x * k, y: c.y + ux.y * k })] });
  parts.push({ kind: "line", centre: true, points: [rp({ x: c.x - uy.x * k, y: c.y - uy.y * k }), rp({ x: c.x + uy.x * k, y: c.y + uy.y * k })] });
  if (jag) parts.push(...jagLidRing(spec, c, turnDeg));
  const ports: FittingPortData[] = [];
  for (const e of spec.ends) {
    const a = rad(e.angle + turnDeg);
    const u = { x: Math.cos(a), y: Math.sin(a) };
    const n = { x: -u.y, y: u.x };
    const h = e.size / 2;
    const w = Math.sqrt(Math.max(0, R * R - h * h)); // where the sides meet the chamber wall
    const spoke = (t: number, s: number): Vec => ({ x: c.x + u.x * t + n.x * s, y: c.y + u.y * t + n.y * s });
    // seen from above, a JagPoly end shows from the edge of the lid ring
    const from = jag ? Math.max(w, Math.sqrt(Math.max(0, lidRing * lidRing - h * h))) : w;
    const f = endFlange(spec, e.size);
    // the run that ends at the face: the spoke itself, or (3.16.1) the run after the bend
    let v = u;
    let face = spoke(R + e.length, 0);
    const centre: Vec[] = [spoke(w, 0)];
    const mitre: Array<Vec | null> = [null, null];
    if (e.bend) {
      const b = rad(e.bend.angle + turnDeg);
      v = { x: Math.cos(b), y: Math.sin(b) };
      const m = { x: -v.y, y: v.x };
      const B = spoke(R + e.length, 0);
      face = { x: B.x + v.x * e.bend.length, y: B.y + v.y * e.bend.length };
      centre.push(B);
      // where side s of the spoke meets side s of the run after the bend
      [1, -1].forEach((s, i) => {
        mitre[i] = meet(spoke(0, s * h), u, { x: B.x + m.x * s * h, y: B.y + m.y * s * h }, v);
      });
    }
    centre.push(face);
    const m = { x: -v.y, y: v.x };
    const atFace = (back: number, s: number): Vec => rp({ x: face.x - v.x * back + m.x * s, y: face.y - v.y * back + m.y * s });
    const back = jag ? f.t : 0;
    [1, -1].forEach((s, i) => {
      const start = rp(spoke(jag ? from : w, s * h));
      const mid = mitre[i];
      parts.push({ kind: "line", centre: false, points: mid ? [start, rp(mid), atFace(back, s * h)] : [start, atFace(back, s * h)] });
    });
    if (jag) parts.push({ kind: "line", centre: false, closed: true, points: [atFace(f.t, f.od / 2), atFace(0, f.od / 2), atFace(0, -f.od / 2), atFace(f.t, -f.od / 2)] });
    else parts.push({ kind: "line", centre: false, points: [atFace(0, 0.6 * e.size), atFace(0, -0.6 * e.size)] });
    parts.push({ kind: "line", centre: true, points: centre.map(rp) });
    ports.push({ p: rp(face), dir: { x: Math.round(v.x * 1e12) / 1e12 || 0, y: Math.round(v.y * 1e12) / 1e12 || 0 }, size: e.size });
  }
  return { parts, ports };
}

/** The JagPoly lid ring seen from above: an outline with 16 notches, and two handle slots (top and bottom of the plan, when no end is there). */
function jagLidRing(spec: LintPotSpec, c: Vec, turnDeg: number): Part[] {
  const D = spec.diameter;
  const Rl = lid(spec).ring;
  const depth = 0.045 * D;
  const N = 16;
  const halfNotch = (0.03 * D) / Rl; // radians
  const pts: Vec[] = [];
  const pol = (r: number, a: number): Vec => rp({ x: c.x + r * Math.cos(a), y: c.y + r * Math.sin(a) });
  const base = rad(turnDeg);
  for (let k = 0; k < N; k++) {
    // a notch, then the rim to the next notch
    const m = base + (2 * Math.PI * (k + 0.5)) / N;
    pts.push(pol(Rl, m - halfNotch), pol(Rl - depth, m - halfNotch), pol(Rl - depth, m + halfNotch), pol(Rl, m + halfNotch));
    const next = base + (2 * Math.PI * (k + 1.5)) / N - halfNotch;
    for (let s = 1; s <= 3; s++) pts.push(pol(Rl, m + halfNotch + ((next - (m + halfNotch)) * s) / 4));
  }
  const parts: Part[] = [{ kind: "line", centre: false, closed: true, points: pts }];
  // the handle slots: a rounded slot in the ring at 90 and 270 degrees (bottom and top of the plan), where no end comes out
  const R = D / 2;
  const slotR = (R + Rl) / 2;
  for (const a0 of [90, 270]) {
    const clear = spec.ends.every((e) => {
      const d = Math.abs(normAngle(e.angle - a0));
      return Math.min(d, 360 - d) > 30;
    });
    if (!clear) continue;
    const a = rad(a0 + turnDeg);
    const u = { x: Math.cos(a), y: Math.sin(a) };
    const n = { x: -u.y, y: u.x };
    const r = 0.03 * D; // half the slot width
    const L2 = 0.16 * D - r; // half the straight part
    const at2 = (t: number, s: number): Vec => rp({ x: c.x + u.x * t + n.x * s, y: c.y + u.y * t + n.y * s });
    const slot: Vec[] = [];
    for (let i = 0; i <= 8; i++) {
      const th = (Math.PI * i) / 8;
      slot.push(at2(slotR - r * Math.cos(th), L2 + r * Math.sin(th)));
    }
    for (let i = 0; i <= 8; i++) {
      const th = (Math.PI * i) / 8;
      slot.push(at2(slotR + r * Math.cos(th), -L2 - r * Math.sin(th)));
    }
    parts.push({ kind: "line", centre: false, closed: true, points: slot });
  }
  return parts;
}

function elevationParts(spec: LintPotSpec, at: Vec, turnDeg: number): { parts: Part[]; ports: FittingPortData[] } {
  const D = spec.diameter;
  const R = D / 2;
  const jag = isJag(spec);
  const H = lintPotHeight(spec);
  const L = lid(spec);
  const top = H - L.stack;
  const ca = Math.cos(rad(turnDeg));
  const sa = Math.sin(rad(turnDeg));
  // elevation frame (x right, y UP) → drawing: turned by turnDeg about `at`
  const P = (x: number, y: number): Vec => rp({ x: at.x + x * ca + y * sa, y: at.y + x * sa - y * ca });
  const dirOf = (x: number, y: number): Vec => ({ x: Math.round((x * ca + y * sa) * 1e12) / 1e12, y: Math.round((x * sa - y * ca) * 1e12) / 1e12 });
  const parts: Part[] = [];
  const line = (pts: Array<[number, number]>, o: { closed?: boolean; hidden?: boolean; centre?: boolean } = {}) =>
    parts.push({ kind: "line", centre: !!o.centre, hidden: !!o.hidden, closed: !!o.closed, points: pts.map(([x, y]) => P(x, y)) });
  const rect = (x0: number, y0: number, x1: number, y1: number, hidden = false) => line([[x0, y0], [x1, y0], [x1, y1], [x0, y1]], { closed: true, hidden });

  // the chamber, the base and the lid
  rect(-R, L.base, R, top);
  if (jag) {
    rect(-R * 1.1, 0, R * 1.1, L.base);
    const Rl = L.ring;
    rect(-Rl, top, Rl, top + L.plate);
    rect(-Rl, H - L.plate, Rl, H);
    // four clamp studs through both plates: eye bolts on the two outside ones, T handles on the two inside ones
    const eye = Math.max(10, 0.035 * D);
    for (const xs of [-0.88, -0.32, 0.32, 0.88]) {
      const x = xs * Rl;
      line([[x, top - 0.04 * D], [x, H + 0.06 * D]]);
      if (Math.abs(xs) > 0.5) {
        const c = P(x, H + 0.06 * D + eye);
        parts.push({ kind: "circle", centre: false, center: c, r: eye });
      } else line([[x - eye, H + 0.06 * D], [x + eye, H + 0.06 * D]]);
    }
  } else {
    const Rl = L.ring;
    rect(-Rl, top, Rl, H);
  }
  // the axis
  line([[0, -0.04 * H], [0, H + 0.08 * H]], { centre: true });

  // the ends. 3.31.0: seen from another side of the plan = the plan turned until that side is at the bottom
  const look = 90 - seenFromAngle(spec);
  const ports: FittingPortData[] = [];
  spec.ends.forEach((e) => {
    // the plan path of the end (3.16.1: with its bend): the elevation shows its x; its plan y says in front or behind
    const path = endPath(spec, e, look);
    const fd = path.faceDir;
    const cx = fd.x;
    const toward = fd.y; // > 0: the face looks towards the person who looks at the elevation
    const h = endHeight(spec, e);
    const d = e.size;
    const f = endFlange(spec, d);
    const t = jag ? f.t : Math.max(6, 0.06 * d);
    const sgn = cx >= 0 ? 1 : -1;
    const F = path.pts[path.pts.length - 1]!;
    const tipX = F.x;
    const behind = F.y < -1e-9;
    // a run between two plan x values at height y: hidden where the chamber is in front of it
    const run = (x0: number, x1: number, y: number, back: boolean, centreLine = false) => {
      if (Math.abs(x1 - x0) < 1) return;
      if (!back) return line([[x0, y], [x1, y]], { centre: centreLine });
      const lo = Math.min(x0, x1), hi = Math.max(x0, x1);
      const hLo = Math.max(lo, -R), hHi = Math.min(hi, R);
      if (hHi <= hLo) return line([[x0, y], [x1, y]], { centre: centreLine });
      line([[hLo, y], [hHi, y]], { hidden: true, centre: centreLine });
      if (lo < hLo) line([[lo, y], [hLo, y]], { centre: centreLine });
      if (hi > hHi) line([[hHi, y], [hi, y]], { centre: centreLine });
    };
    // the stub (and the run after the bend): two lines at the height of the end, the centre line between them
    for (let i = 0; i + 1 < path.pts.length; i++) {
      const A = path.pts[i]!;
      const B = path.pts[i + 1]!;
      const last = i + 2 === path.pts.length;
      const dir = path.dirs[i]!;
      const x1 = last ? B.x - dir.x * t : B.x;
      const back = (A.y + B.y) / 2 < -1e-9;
      for (const s of [-1, 1]) run(A.x, x1, h + (s * d) / 2, back);
      run(A.x, last ? B.x + sgn * 0.15 * d * Math.abs(cx) : B.x, h, back, true);
    }
    // the flange: from the side a plate; turned towards you or away from you its face, an ellipse
    const faceHidden = behind && Math.abs(tipX) + (f.od / 2) * Math.abs(toward) <= R + 1e-9;
    if (Math.abs(toward) < 0.2) {
      rect(Math.min(tipX, tipX - sgn * t), h - f.od / 2, Math.max(tipX, tipX - sgn * t), h + f.od / 2, behind && Math.abs(tipX) <= R);
    } else {
      const k = Math.abs(toward);
      parts.push({ kind: "line", centre: false, hidden: faceHidden, closed: true, points: ellipse({ x: tipX, y: h }, (f.od / 2) * k, f.od / 2).map((q) => P(q.x, q.y)) });
      parts.push({ kind: "line", centre: false, hidden: faceHidden, closed: true, points: ellipse({ x: tipX, y: h }, (d / 2) * k, d / 2).map((q) => P(q.x, q.y)) });
      if (k > 0.9 && f.n > 0 && !behind) {
        for (let i = 0; i < f.n; i++) {
          const b = rad(-90 + (360 * (i + 0.5)) / f.n);
          parts.push({ kind: "circle", centre: false, center: P(tipX + (f.pcd / 2) * k * Math.cos(b), h + (f.pcd / 2) * Math.sin(b)), r: f.hole / 2 });
        }
      }
      if (!faceHidden) {
        line([[tipX - 0.6 * (f.od / 2) * k, h], [tipX + 0.6 * (f.od / 2) * k, h]], { centre: true });
        line([[tipX, h - 0.6 * (f.od / 2)], [tipX, h + 0.6 * (f.od / 2)]], { centre: true });
      }
    }
    // the end: along the drawing when the end goes to the side; VERTICAL (out of the drawing) when it points at you or away
    const vertical = Math.abs(cx) < 0.35;
    ports.push({ p: P(tipX, h), dir: dirOf(sgn, 0), size: d, ...(vertical ? { vertical: true } : {}) });
  });
  return { parts, ports };
}

/** Where two lines p + t·d and q + t·e meet; null when they are parallel. */
function meet(p: Vec, d: Vec, q: Vec, e: Vec): Vec | null {
  const den = d.x * e.y - d.y * e.x;
  if (Math.abs(den) < 1e-12) return null;
  const t = ((q.x - p.x) * e.y - (q.y - p.y) * e.x) / den;
  return { x: p.x + d.x * t, y: p.y + d.y * t };
}

/**
 * The centre line of an end in the plan, from the chamber wall to the face, the chamber centre at 0,0 (3.16.1): the
 * points (wall, bend, face; or wall, face), the direction of each run, and the direction the face looks.
 */
export function endPath(spec: LintPotSpec, e: LintPotEnd, turnDeg = 0): { pts: Vec[]; dirs: Vec[]; faceDir: Vec } {
  const R = spec.diameter / 2;
  const a = rad(e.angle + turnDeg);
  const u = { x: Math.cos(a), y: Math.sin(a) };
  const W = { x: R * u.x, y: R * u.y };
  const B = { x: (R + e.length) * u.x, y: (R + e.length) * u.y };
  if (!e.bend) return { pts: [W, B], dirs: [u], faceDir: u };
  const b = rad(e.bend.angle + turnDeg);
  const v = { x: Math.cos(b), y: Math.sin(b) };
  return { pts: [W, B, { x: B.x + v.x * e.bend.length, y: B.y + v.y * e.bend.length }], dirs: [u, v], faceDir: v };
}

/**
 * A bend for an end that has none (3.16.1): the face turns to the nearest of right, down, left and up that is 10° or
 * more away (45° on a diagonal end, 90° on a square end); after the bend, the default end length or the least it can be.
 */
export function defaultBend(spec: LintPotSpec, e: LintPotEnd): { angle: number; length: number } {
  const a = normAngle(e.angle);
  const near = Math.round(a / 90) * 90;
  const angle = normAngle(Math.abs(a - near) >= 10 ? near : near + 90);
  const turn = Math.abs(bendTurn({ ...e, bend: { angle, length: 0 } }));
  const need = (e.size / 2) * Math.tan(rad(turn) / 2) + (isJag(spec) ? tableEFlange(e.size).t : 0) + 10;
  return { angle, length: Math.max(defaultEndLength(e.size, spec), Math.ceil(need / 10) * 10) };
}

/** The turn of a bend, degrees, -180 < turn <= 180 (positive = clockwise on the screen). */
export const bendTurn = (e: LintPotEnd): number => {
  if (!e.bend) return 0;
  const d = normAngle(e.bend.angle - e.angle);
  return d > 180 ? d - 360 : d;
};

/** Parts as entities on `layerId`, with ids from `makeId`. */
export function lintPotEntities(
  spec: LintPotSpec,
  makeId: () => string,
  layerId: string,
  at: Vec = { x: 0, y: 0 },
  turnDeg = 0,
  styles: { outline: Style; centre: Style } = { outline: OUTLINE, centre: CENTRE }
): { entities: Entity[]; ports: FittingPortData[] } {
  const { parts, ports } = lintPotParts(spec, at, turnDeg);
  const hidden: Style = { ...styles.outline, dash: "dashed", strokeWidth: Math.min(1, styles.outline.strokeWidth), opacity: 0.8 };
  const entities: Entity[] = parts.map((p) => {
    const style = structuredClone(p.centre ? styles.centre : p.hidden ? hidden : styles.outline);
    if (p.kind === "circle") return { id: makeId(), type: "circle", layerId, locked: false, hidden: false, center: p.center!, r: p.r!, style };
    return { id: makeId(), type: "polyline", layerId, locked: false, hidden: false, points: p.points!, closed: !!p.closed, style };
  });
  return { entities, ports };
}

/** meta.fitting of a lint pot: its first inlet and its first outlet (for the library filters). */
export function lintPotFitting(spec: LintPotSpec): { kind: "lintpot"; size: number; size2?: number } {
  const inlet = spec.ends.find((e) => e.role === "inlet") ?? spec.ends[0]!;
  const outlet = spec.ends.find((e) => e.role === "outlet");
  return { kind: "lintpot", size: inlet.size, ...(outlet && outlet.size !== inlet.size ? { size2: outlet.size } : {}) };
}

/** "inlet 150 mm · 2 outlets 100 mm" */
export function lintPotEndWords(spec: LintPotSpec): string {
  const words = (role: EndRole): string | null => {
    const list = spec.ends.filter((e) => e.role === role);
    if (list.length === 0) return null;
    const sizes = [...new Set(list.map((e) => e.size))].join(", ");
    return `${list.length === 1 ? role : `${list.length} ${role}s`} ${sizes} mm`;
  };
  return [words("inlet"), words("outlet")].filter(Boolean).join(" · ");
}

/** "Lint pot · 400 mm · inlet 150 mm · 2 outlets 100 mm", "JagPoly lint pot 30L · 400 mm · ... · elevation from the left" */
export const lintPotName = (spec: LintPotSpec): string =>
  `${isJag(spec) ? `JagPoly lint pot${spec.model ? ` ${spec.model}` : ""}` : "Lint pot"} · ${spec.diameter} mm · ${lintPotEndWords(spec)}${spec.view === "elevation" ? ` · elevation${seenFromWords(spec)}` : ""}`;

/** A lint pot as a library item, centred on 0,0. */
export function makeLintPotItem(spec: LintPotSpec, key: string, name = lintPotName(spec), category = isJag(spec) ? JAGPOLY_CATEGORY : LINTPOT_CATEGORY): LibraryItem {
  const problem = lintPotProblem(spec);
  if (problem) throw new Error(`lint pot ${key}: ${problem}`);
  // Centred on its box, as every library item is (the list picture and the zoom after placing need it). The chamber
  // centre (plan) or the centre of the base (elevation) is the turning point.
  const first = lintPotEntities(spec, () => "x", "fittings").entities;
  let n = 0;
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  const add = (p: Vec) => {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  };
  for (const e of first) {
    if (e.type === "polyline") e.points.forEach(add);
    else if (e.type === "circle") {
      add({ x: e.center.x - e.r, y: e.center.y - e.r });
      add({ x: e.center.x + e.r, y: e.center.y + e.r });
    }
  }
  const at = { x: round(-(minX + maxX) / 2), y: round(-(minY + maxY) / 2) };
  const { entities, ports } = lintPotEntities(spec, () => `lintpot-${key}-${++n}`, "fittings", at);
  entities.push({
    id: `lintpot-${key}-group`,
    type: "group",
    layerId: "fittings",
    locked: false,
    hidden: false,
    children: entities.map((e) => e.id),
    meta: { fitting: lintPotFitting(spec), lintpot: structuredClone(spec), pivot: at, ports }
  });
  return {
    id: `builtin:lintpot-${key}`,
    name,
    category,
    entities,
    size: { w: Math.round((maxX - minX) * 10) / 10, h: Math.round((maxY - minY) * 10) / 10 },
    units: "mm",
    updatedAt: "2026-10-20T00:00:00.000Z"
  };
}

const end = (role: EndRole, size: number, angle: number, length = defaultEndLength(size)): LintPotEnd => ({ role, size, angle, length });

/**
 * The lint pots in the library. Each one is a start: select it on the drawing and change the chamber and every end in
 * the Inspector.
 */
export const LINTPOT_PRESETS: Array<{ key: string; name: string; spec: LintPotSpec }> = [
  {
    key: "2-inline",
    name: "Lint pot · 2 ends in line · 400 mm, ends 100 mm",
    spec: { diameter: 400, ends: [end("inlet", 100, 180), end("outlet", 100, 0)] }
  },
  {
    key: "2-corner",
    name: "Lint pot · 2 ends at 90° · 400 mm, ends 100 mm",
    spec: { diameter: 400, ends: [end("inlet", 100, 180), end("outlet", 100, 90)] }
  },
  {
    key: "3-split",
    name: "Lint pot · 3 ends · inlet 150 mm, 2 outlets 100 mm at 45°",
    spec: { diameter: 450, ends: [end("inlet", 150, 180, 150), end("outlet", 100, 315, 150), end("outlet", 100, 45, 150)] }
  },
  {
    key: "4-cross",
    name: "Lint pot · 4 ends · 400 mm, ends 100 mm",
    spec: { diameter: 400, ends: [end("inlet", 100, 180), end("outlet", 100, 270), end("outlet", 100, 0), end("outlet", 100, 90)] }
  },
  {
    // the owner's sketch of 20 Oct 2026: DN200 on one side, two DN150 on the other that leave at 45° and turn to face the same way
    key: "3-bent",
    name: "Lint pot · inlet 200 mm, 2 outlets 150 mm with bends, both facing left",
    spec: {
      diameter: 450,
      ends: [
        end("inlet", 200, 0),
        { role: "outlet", size: 150, angle: 225, length: 150, bend: { angle: 180, length: 250 } },
        { role: "outlet", size: 150, angle: 135, length: 150, bend: { angle: 180, length: 250 } }
      ]
    }
  }
];

// ------------------------------------------------------------------------------------------- JagPoly --

/**
 * The JagPoly configurations from the owner's scan (2026-10-20, six plans with their elevations; red notes: DN150 T/E
 * ends, the high end at 650 mm (30L) or 675 mm (60L) and the low end at 338 mm above the base, a DN200 T/E inlet on the
 * 3-end split). Configurations 3 and 6 have bends on their outlets (3.16.1: drawn with the end bend).
 *
 * CHAMBER: 400 mm diameter, 1000 mm high (base to the top of the lid). SOURCE: the owner, 2026-10-20, answer to
 * information request 257d2970: "JagPoly lint pots are generally 1000 mm high and 400 mm in diameter." This replaces
 * the 3.16.0 ESTIMATE (350 mm, 910 mm; 60L 935 mm) that was measured from the scan. The 60L preset uses the same
 * values, because the owner gave one size for JagPoly pots.
 *
 * STILL ESTIMATED (not in the scan and not given by the owner): the end lengths from the wall (defaultEndLength), the
 * bend lengths (200 mm), the end angles (read from the plan drawings, no numbers given), and the lid ring, lid stack
 * and eye bolt proportions (lid()). Change them in the Inspector when the JagPoly data sheet gives them.
 */
const JAG_D = 400;
/** Base to the top of the lid, mm (owner, 2026-10-20). */
const JAG_H = 1000;
const jag = (role: EndRole, size: number, angle: number, height: number): LintPotEnd => ({ role, size, angle, length: defaultEndLength(size, { make: "jagpoly" }), height });

/**
 * 3.31.0: the JagPoly LLPD050, drawing LLPD-050225160-S7 rev 0 (21/10/26), "50Lps dual outlet Leaf and lint Pot", file
 * "LLPD050 straight exit 225-160" (owner, 2026-10-27: "another jag poly lint pot config ... one for plan view, the other
 * elevations"). 225-160: the PVC outside diameters of DN200 and DN150.
 *
 * FROM THE DRAWING (dimensioned): chamber 400 mm; 1012 mm from the base to the top of the lid; inlet DN200 at 675 mm;
 * outlets DN150 at 338 mm; 546 mm between the outlet centres; 806 mm from the inlet face to the outlet faces.
 * MEASURED FROM THE DRAWING (no dimension, about ±5 mm, drawn to scale at 1.21 mm a pixel at 300 dpi): the inlet face
 * 375 mm from the chamber centre (length 175 from the wall); the outlets leave at 45° (225° and 315°) and turn to face
 * the same way, up the plan, at 273 mm each side of the centre (length 186.1 to the bend), the faces 431 mm from the
 * centre (158 after the bend: 375 + 431 = 806). NOT DRAWN: the small drain spigot near the base below the inlet, and the
 * elbows are drawn as mitre bends. The flanges are Table E, as on every JagPoly pot here.
 */
const LLPD050: LintPotSpec = {
  diameter: 400,
  height: 1012,
  make: "jagpoly",
  model: "LLPD050",
  ends: [
    { role: "inlet", size: 200, angle: 90, length: 175, height: 675 },
    { role: "outlet", size: 150, angle: 225, length: 186.1, height: 338, bend: { angle: 270, length: 158 } },
    { role: "outlet", size: 150, angle: 315, length: 186.1, height: 338, bend: { angle: 270, length: 158 } }
  ]
};

/** `elevations`: the sides each elevation is seen from (SEEN_FROM angles). Absent = the front only. */
export const JAGPOLY_PRESETS: Array<{ key: string; name: string; spec: LintPotSpec; elevations?: number[] }> = [
  {
    key: "jag-1-inline",
    name: "JagPoly 30L · 1 · inlet and outlet opposite · DN150 T/E",
    spec: { diameter: JAG_D, height: JAG_H, make: "jagpoly", model: "30L", ends: [jag("inlet", 150, 180, 650), jag("outlet", 150, 0, 338)] }
  },
  {
    key: "jag-2-corner",
    name: "JagPoly 30L · 2 · inlet at the side, outlet at the front · DN150 T/E",
    spec: { diameter: JAG_D, height: JAG_H, make: "jagpoly", model: "30L", ends: [jag("inlet", 150, 0, 650), jag("outlet", 150, 90, 338)] }
  },
  {
    key: "jag-3-bends",
    name: "JagPoly 30L · 3 · inlet at the side, 2 outlets with bends, back and front · DN150 T/E",
    spec: { diameter: JAG_D, height: JAG_H, make: "jagpoly", model: "30L", ends: [jag("inlet", 150, 0, 650), { ...jag("outlet", 150, 225, 338), bend: { angle: 270, length: 200 } }, { ...jag("outlet", 150, 135, 338), bend: { angle: 90, length: 200 } }] }
  },
  {
    key: "jag-4-split-30l",
    name: "JagPoly 30L · 4 · inlet DN200 T/E, 2 outlets DN150 T/E at 45°",
    spec: { diameter: JAG_D, height: JAG_H, make: "jagpoly", model: "30L", ends: [jag("inlet", 200, 180, 650), jag("outlet", 150, 315, 338), jag("outlet", 150, 45, 338)] }
  },
  {
    key: "jag-4-split-60l",
    name: "JagPoly 60L · 4 · inlet DN200 T/E, 2 outlets DN150 T/E at 45°",
    spec: { diameter: JAG_D, height: JAG_H, make: "jagpoly", model: "60L", ends: [jag("inlet", 200, 180, 675), jag("outlet", 150, 315, 338), jag("outlet", 150, 45, 338)] }
  },
  {
    key: "jag-5-three",
    name: "JagPoly 30L · 5 · inlet at the side, outlets opposite and at the front · DN150 T/E",
    spec: { diameter: JAG_D, height: JAG_H, make: "jagpoly", model: "30L", ends: [jag("inlet", 150, 0, 650), jag("outlet", 150, 180, 338), jag("outlet", 150, 90, 338)] }
  },
  {
    key: "jag-6-y",
    name: "JagPoly 30L · 6 · inlet, 2 outlets in a Y with bends · DN150 T/E",
    spec: { diameter: JAG_D, height: JAG_H, make: "jagpoly", model: "30L", ends: [jag("inlet", 150, 180, 650), { ...jag("outlet", 150, 300, 338), bend: { angle: 270, length: 200 } }, { ...jag("outlet", 150, 60, 338), bend: { angle: 90, length: 200 } }] }
  },
  {
    // 3.31.0: the three elevations of the drawing: front (the inlet side), left side, back (the outlet side)
    key: "jag-llpd050",
    name: "JagPoly LLPD050 · 50 L/s dual outlet · inlet DN200, 2 outlets DN150 turned to face the same way",
    spec: LLPD050,
    elevations: [90, 180, 270]
  }
];

let cache: LibraryItem[] | null = null;
export function builtInLintPots(): LibraryItem[] {
  if (!cache) {
    cache = LINTPOT_PRESETS.map((p) => makeLintPotItem(p.spec, p.key, p.name));
    for (const p of JAGPOLY_PRESETS) {
      cache.push(makeLintPotItem(p.spec, p.key, `${p.name} · plan`));
      for (const from of p.elevations ?? [90]) {
        const spec: LintPotSpec = { ...p.spec, view: "elevation", ...(from !== 90 ? { seenFrom: from } : {}) };
        cache.push(makeLintPotItem(spec, from === 90 ? `${p.key}-elev` : `${p.key}-elev-${from}`, `${p.name} · elevation${seenFromWords(spec)}`));
      }
    }
  }
  return cache;
}
